/**
 * Leaving the old permanent-event hosts (MEMO-133). Guests of permanent venues used `live.gomemo.io`
 * (`permanent-dev.<tenant>.gomemo.io` on dev), and venues' printed QR codes still point there. The one
 * guest app now lives on the selfie host under `/p/`. A guest's visitor code and sign-in are kept in
 * that old host's localStorage, which the new host can't read, so the old host hands them over:
 *
 * - on an old host, the app runs no flow: it reads the permanent flow's saved state (`memo.*`) and
 *   replaces the address with `<guest origin>/p/<path>#memo-handoff=<payload>` (no history entry);
 *   with nothing saved it forwards plainly;
 * - on the new host, before anything renders, the fragment is stripped from the address, and its
 *   values are stored only where this origin has nothing yet. It is taken only from an old host
 *   (the referrer, same-site, so browsers keep it): a link carrying a payload from anywhere else
 *   can't plant someone else's album on a guest's device.
 *
 * Old hosts are VITE_PERMANENT_HOSTS (as for the flow fallback); the new origin is VITE_GUEST_ORIGIN,
 * with `{tenant}` taken from the old host's tenant label. Without VITE_GUEST_ORIGIN nothing moves.
 * The fragment never reaches a server.
 */

const PARAM = 'memo-handoff'
const VERSION = 1
/** The permanent flow's own keys (visitor code, sign-ins, language, checkout details). */
const FLOW_KEY = /^memo\./
/** A URL fragment past this is trimmed to the essentials: the visitor code and the sign-ins. */
const MAX_PAYLOAD = 16_000
const TENANT_HOST = /^([a-z0-9-]+)\.([a-z0-9-]+)\.(gomemo\.io)$/

type Payload = { v: number; items: Record<string, string> }

/** `permanent-dev.il.gomemo.io` counts as `permanent-dev.gomemo.io`, as in guestMode. */
function sharedName(hostname: string): string {
  return hostname.toLowerCase().replace(TENANT_HOST, '$1.$3')
}

function isOldHost(hostname: string, oldHosts: string): boolean {
  const hosts = oldHosts.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
  return hosts.includes(sharedName(hostname))
}

/** Where an old host forwards to, or null when this is not an old host (or nothing is configured). */
export function handoffTarget(hostname: string, oldHosts = '', guestOrigin = ''): string | null {
  if (!guestOrigin || !isOldHost(hostname, oldHosts)) return null
  if (!guestOrigin.includes('{tenant}')) return guestOrigin.replace(/\/+$/, '')
  const tenant = TENANT_HOST.exec(hostname.toLowerCase())?.[2]
  return tenant ? guestOrigin.replace('{tenant}', tenant).replace(/\/+$/, '') : null
}

function encode(p: Payload): string {
  const bytes = new TextEncoder().encode(JSON.stringify(p))
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function decode(s: string): Payload | null {
  try {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'))
    const p = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)))) as Payload
    return p && p.v === VERSION && p.items && typeof p.items === 'object' ? p : null
  } catch {
    return null
  }
}

/** The flow's saved state as a fragment value, or '' when there is none. */
export function collectHandoff(storage: Storage): string {
  const items: Record<string, string> = {}
  try {
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      const v = k === null ? null : storage.getItem(k)
      if (k && v !== null && FLOW_KEY.test(k)) items[k] = v
    }
  } catch {
    return ''
  }
  if (Object.keys(items).length === 0) return ''
  let out = encode({ v: VERSION, items })
  if (out.length > MAX_PAYLOAD) {
    const essential = Object.fromEntries(
      Object.entries(items).filter(([k]) => k === 'memo.visitorCode' || k.startsWith('memo.auth.')),
    )
    out = Object.keys(essential).length ? encode({ v: VERSION, items: essential }) : ''
  }
  return out
}

/** The address an old host's page moves to: same path under `/p`, same query, the payload as fragment. */
export function forwardUrl(target: string, loc: Pick<Location, 'pathname' | 'search' | 'hash'>, payload: string): string {
  const path = loc.pathname === '/' ? '/' : loc.pathname.startsWith('/p/') ? loc.pathname : `/p${loc.pathname}`
  const hash = payload ? `#${PARAM}=${payload}` : loc.hash
  return target + path + loc.search + hash
}

/**
 * On the new host: take a payload from the fragment, if any. The fragment always leaves the address;
 * the values are stored only when the page was opened from an old host, and only where this origin
 * has nothing saved. Returns how many values were stored.
 */
export function acceptHandoff(storage: Storage, referrer: string, oldHosts = ''): number {
  const { hash, pathname, search } = window.location
  const prefix = `#${PARAM}=`
  if (!hash.startsWith(prefix)) return 0
  window.history.replaceState(window.history.state, '', pathname + search)

  let fromOldHost = false
  try {
    fromOldHost = referrer !== '' && isOldHost(new URL(referrer).hostname, oldHosts)
  } catch {
    fromOldHost = false
  }
  const p = fromOldHost ? decode(hash.slice(prefix.length)) : null
  if (!p) return 0

  let stored = 0
  try {
    for (const [k, v] of Object.entries(p.items)) {
      if (!FLOW_KEY.test(k) || typeof v !== 'string' || storage.getItem(k) !== null) continue
      storage.setItem(k, v)
      stored++
    }
  } catch {
    // storage blocked or full: the guest can still recover their album
  }
  return stored
}
